import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { History, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { apiRequest, queryClient } from "@/lib/queryClient";
import {
  normalizeVideoReferencePromptConfiguration,
  VIDEO_REFERENCE_PROMPT_TYPES,
  type VideoReferencePromptType,
  type VideoReferencePromptConfiguration,
} from "@/lib/video-reference-workflow-response";

export function videoReferenceBrandPromptUrl(brandId: string, type: VideoReferencePromptType = "general") {
  return `/api/admin/video-reference-workflows/${brandId}/prompts?type=${encodeURIComponent(type)}`;
}

export function videoReferenceBrandPromptQueryKey(
  brandId: string,
  type: VideoReferencePromptType = "general",
) {
  return [videoReferenceBrandPromptUrl(brandId, type)];
}

export function useVideoReferenceBrandPromptConfiguration(
  brandId: string | undefined,
  type: VideoReferencePromptType = "general",
) {
  const base = brandId ? videoReferenceBrandPromptUrl(brandId, type) : "";
  return useQuery<VideoReferencePromptConfiguration>({
    queryKey: videoReferenceBrandPromptQueryKey(brandId || "", type),
    enabled: !!brandId,
    queryFn: async () => {
      const response = await apiRequest("GET", base);
      return normalizeVideoReferencePromptConfiguration(await response.json());
    },
  });
}

export function VideoReferenceBrandPromptDialog({
  brandId,
  brandName,
  canUseSimFallback: canUseSimFallbackFromWorkflow,
  typeSplitEnabled = true,
  initialType = "general",
  open,
  onOpenChange,
}: {
  brandId: string | undefined;
  brandName?: string;
  typeSplitEnabled?: boolean;
  initialType?: VideoReferencePromptType;
  /**
   * The workflow list can advertise fallback capability before the prompt
   * endpoint has been refreshed. Either API response may provide the field.
   */
  canUseSimFallback?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const base = brandId ? videoReferenceBrandPromptUrl(brandId) : "";
  const [promptType, setPromptType] = useState<VideoReferencePromptType>(
    typeSplitEnabled ? initialType : "general",
  );
  const [drafts, setDrafts] = useState<Partial<Record<VideoReferencePromptType, string>>>({});
  const loadedVersionByType = useRef<Partial<Record<VideoReferencePromptType, string>>>({});
  const baselineByType = useRef<Partial<Record<VideoReferencePromptType, string>>>({});
  const generalConfiguration = useVideoReferenceBrandPromptConfiguration(
    open ? brandId : undefined,
    "general",
  );
  const ugcConfiguration = useVideoReferenceBrandPromptConfiguration(
    open && promptType === "ugc" ? brandId : undefined,
    "ugc",
  );
  const threeDConfiguration = useVideoReferenceBrandPromptConfiguration(
    open && promptType === "3d" ? brandId : undefined,
    "3d",
  );
  const configurations: Record<
    VideoReferencePromptType,
    ReturnType<typeof useVideoReferenceBrandPromptConfiguration>
  > = {
    general: generalConfiguration,
    ugc: ugcConfiguration,
    "3d": threeDConfiguration,
  };
  const { data, isLoading, error, refetch } = configurations[promptType];
  const generalData = configurations.general.data;
  const draft = drafts[promptType] ?? "";
  const [actionError, setActionError] = useState<string | null>(null);
  const currentText = data?.currentVersion?.promptText ?? "";
  const dirty = !!data && draft !== currentText;
  const dirtyAny = VIDEO_REFERENCE_PROMPT_TYPES.some((type) => {
    const baseline = baselineByType.current[type];
    return baseline !== undefined && (drafts[type] ?? baseline) !== baseline;
  });
  const canUseSimFallback =
    data?.canUseSimFallback === true || canUseSimFallbackFromWorkflow === true;

  useEffect(() => {
    if (!typeSplitEnabled) setPromptType("general");
  }, [typeSplitEnabled]);

  useEffect(() => {
    if (open) setPromptType(typeSplitEnabled ? initialType : "general");
  }, [open, initialType, typeSplitEnabled]);

  useEffect(() => {
    if (!open || !data) return;
    const versionKey = `${data.brandId}:${data.currentVersion?.id ?? "empty"}`;
    if (loadedVersionByType.current[promptType] === versionKey) return;
    loadedVersionByType.current[promptType] = versionKey;
    baselineByType.current[promptType] = data.currentVersion?.promptText ?? "";
    setDrafts((current) => ({
      ...current,
      [promptType]: data.currentVersion?.promptText ?? "",
    }));
  }, [open, data?.brandId, data?.currentVersion?.id, promptType]);

  useEffect(() => {
    if (!dirtyAny) return;
    const protect = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", protect);
    return () => window.removeEventListener("beforeunload", protect);
  }, [dirtyAny]);

  const close = () => {
    if (dirtyAny && !window.confirm("Discard your unsaved prompt changes?")) return;
    setDrafts({});
    loadedVersionByType.current = {};
    baselineByType.current = {};
    onOpenChange(false);
  };

  const mutation = useMutation({
    mutationFn: async (action: { kind: "save" | "reset" | "restore" | "clear"; versionId?: string }) => {
      let method = "POST";
      let url = base.split("?")[0];
      let body: Record<string, string> = { type: promptType };
      if (action.kind === "save") body = { ...body, promptText: draft.trim() };
      if (action.kind === "reset") url += "/reset";
      if (action.kind === "restore") url += `/${action.versionId}/restore`;
      if (action.kind === "clear") {
        method = "DELETE";
        url += "/current";
      }
      const response = await apiRequest(method, url, body);
      if (response.status !== 204) await response.json();
      return action;
    },
    onSuccess: (action) => {
      if (action.kind === "save") {
        setDrafts((current) => ({ ...current, [promptType]: draft.trim() }));
        baselineByType.current[promptType] = draft.trim();
      }
      queryClient.invalidateQueries({ queryKey: videoReferenceBrandPromptQueryKey(brandId || "", promptType) });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/video-reference-workflows"] });
      setActionError(null);
    },
    onError: (mutationError: Error) => setActionError(mutationError.message),
  });

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) close(); }}>
      <DialogContent
        className="flex h-[min(88dvh,900px)] w-[calc(100vw-2rem)] max-w-3xl flex-col gap-4 overflow-hidden p-4 sm:p-6"
        onInteractOutside={(event) => { if (dirtyAny) event.preventDefault(); }}
        data-testid="dialog-vra-prompt-configuration"
      >
         <DialogHeader className="shrink-0 text-left">
           <DialogTitle className="flex items-center gap-2 text-lg"><Sparkles className="h-5 w-5 text-violet-600" /> Manage brand prompts</DialogTitle>
           <DialogDescription>
             Configure the wording used for {brandName || data?.brandName || "this brand"}. Each save creates a new version.
           </DialogDescription>
         </DialogHeader>
        <Tabs
          value={promptType}
          onValueChange={(value) => setPromptType(value as VideoReferencePromptType)}
           className="min-w-0 shrink-0"
        >
           <TabsList aria-label="Prompt type" className="grid h-auto w-full grid-cols-3 rounded-lg border bg-muted/30 p-1">
            {(typeSplitEnabled ? VIDEO_REFERENCE_PROMPT_TYPES : ["general"] as const).map((type) => {
              const version = configurations[type].data?.currentVersion;
              const hasLoaded = !!configurations[type].data;
              return (
                 <TabsTrigger key={type} value={type} disabled={mutation.isPending} className="min-w-0 flex-col gap-0.5 px-1 py-2 text-xs data-[state=active]:bg-violet-50 data-[state=active]:text-violet-700 data-[state=active]:shadow-sm sm:flex-row sm:text-sm" data-testid={`tab-vra-prompt-${type}`}>
                  {type === "3d" ? "3D" : type === "ugc" ? "UGC" : "General"}
                   <Badge variant={version ? "secondary" : "outline"} className="px-1.5 py-0 text-[10px] sm:ml-2">
                    {version ? `v${version.versionNumber}` : hasLoaded ? "Empty" : "…"}
                  </Badge>
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>
        {isLoading ? (
          <div className="flex min-h-48 items-center justify-center gap-2 text-sm text-muted-foreground" role="status">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading prompt configuration…
          </div>
        ) : error ? (
          <div className="space-y-3 rounded-md border border-destructive/40 p-4" role="alert">
            <p className="text-sm text-destructive">{(error as Error).message}</p>
            <Button size="sm" variant="outline" onClick={() => refetch()}>Retry</Button>
          </div>
        ) : data ? (
           <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
             <div className="flex min-h-0 flex-col gap-3 rounded-xl border border-violet-100 bg-violet-50/30 p-3 sm:p-4">
               <div className="flex flex-wrap items-start justify-between gap-2">
                 <div>
                   <Label htmlFor="vra-prompt-editor" className="text-sm font-semibold">{promptType === "3d" ? "3D" : promptType === "ugc" ? "UGC" : "General"} prompt</Label>
                   <p className="mt-1 text-xs text-muted-foreground">
                     {data.currentVersion
                       ? `Current version ${data.currentVersion.versionNumber} · ${data.currentVersion.createdAt ? new Date(data.currentVersion.createdAt).toLocaleString() : "Date unavailable"}`
                       : promptType === "general"
                         ? "No active General prompt. Type-aware runs require a reviewed General version; legacy runs retain their Sim workflow."
                         : "No active prompt. New runs use the General prompt."}
                   </p>
                 </div>
                 <Badge className={data.currentVersion ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : ""} variant={data.currentVersion ? "default" : "outline"}>
                   {data.currentVersion ? "Active" : promptType === "general" ? "Not set" : "General fallback"}
                 </Badge>
               </div>
              {promptType !== "general" && !data.currentVersion && !draft.trim() ? (
                <div className="flex items-center justify-between gap-2 rounded-md border border-dashed p-2 text-xs">
                  <span className="text-muted-foreground">No {promptType.toUpperCase()} prompt is saved. Clone General as a starting point.</span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={!generalData?.currentVersion || mutation.isPending}
                    onClick={() => {
                      setDrafts((current) => ({
                        ...current,
                        [promptType]: generalData?.currentVersion?.promptText ?? "",
                      }));
                      setActionError(null);
                    }}
                    data-testid="button-clone-general-vra-prompt"
                  >
                    Clone from General
                  </Button>
                </div>
              ) : null}
              <Textarea
                id="vra-prompt-editor"
                autoFocus
                 className="min-h-[180px] w-full resize-y bg-background font-mono text-xs leading-relaxed focus-visible:border-violet-500 focus-visible:ring-violet-500 sm:min-h-[260px]"
                value={draft}
                onChange={(event) => {
                  setDrafts((current) => ({ ...current, [promptType]: event.target.value }));
                  setActionError(null);
                }}
                disabled={mutation.isPending}
                data-testid="textarea-vra-prompt"
              />
               <p className="text-xs text-muted-foreground">{draft.length.toLocaleString()} characters{dirty ? " · Unsaved changes" : ""}</p>
               {actionError ? <p role="alert" className="text-sm text-destructive">{actionError}</p> : null}
             </div>
             <details className="rounded-xl border bg-background p-3 sm:p-4" data-testid="section-vra-prompt-advanced">
               <summary className="cursor-pointer text-sm font-medium text-foreground">Version history & advanced actions</summary>
               <div className="mt-4 space-y-4">
               <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => {
                  if (!dirty || window.confirm("Discard your unsaved prompt changes and start blank?")) {
                    setDrafts((current) => ({ ...current, [promptType]: "" }));
                    setActionError(null);
                  }
                }} disabled={mutation.isPending} data-testid="button-start-blank-vra-prompt">Start blank</Button>
                <Button variant="outline" onClick={() => {
                  if (!dirty || window.confirm("Discard your unsaved changes and reset to the starter prompt?")) mutation.mutate({ kind: "reset" });
                }} disabled={mutation.isPending}>Reset to starter</Button>
                <Button variant="outline" onClick={() => {
                  const message = dirty
                     ? `Discard your unsaved changes and clear the active prompt? ${promptType === "general" ? "New type-aware runs will be blocked until you publish a reviewed General prompt. The Sim workflow stays configured for legacy runs." : "New runs will use the General prompt."}`
                     : `Clear the active prompt? ${promptType === "general" ? "New type-aware runs will be blocked until you publish a reviewed General prompt. The Sim workflow stays configured for legacy runs." : "New runs will use the General prompt."}`;
                  if (window.confirm(message)) mutation.mutate({ kind: "clear" });
                }} disabled={mutation.isPending || !data.currentVersion || (promptType === "general" && !canUseSimFallback)}
                  data-testid="button-clear-vra-prompt">
                  {promptType === "general" ? "Clear General prompt" : "Clear to General fallback"}
                </Button>
              </div>
               {promptType === "general" && data.currentVersion && !canUseSimFallback ? (
                <p
                  className="text-xs text-muted-foreground"
                  data-testid="text-clear-vra-prompt-disabled"
                >
                  Sim fallback is not available for this brand, so the active prompt cannot be
                  cleared. Ask an administrator to configure a usable Sim fallback first.
                </p>
              ) : null}
             <aside className="space-y-2 border-t pt-4" aria-label="Prompt version history">
              <h4 className="flex items-center gap-1.5 text-sm font-medium"><History className="h-4 w-4" /> Version history</h4>
              {data.versions.length === 0 ? <p className="text-xs text-muted-foreground">No saved versions yet.</p> : data.versions.map((version) => (
                <div key={version.id} className="rounded-md border p-3" data-testid={`prompt-version-${version.versionNumber}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium">Version {version.versionNumber}</span>
                    {data.currentVersion?.id === version.id ? <Badge>Current</Badge> : null}
                  </div>
                  <p className="mt-1 text-[11px] text-muted-foreground">{version.createdAt ? new Date(version.createdAt).toLocaleString() : "Date unavailable"}{version.createdByName ? ` · ${version.createdByName}` : ""}</p>
                  {data.currentVersion?.id !== version.id ? <Button className="mt-2 h-7 px-2 text-xs" size="sm" variant="outline" disabled={mutation.isPending} onClick={() => {
                    if (!dirty || window.confirm(`Discard your unsaved changes and reset to version ${version.versionNumber}? This creates a new version.`)) mutation.mutate({ kind: "restore", versionId: version.id });
                  }}>Reset to version {version.versionNumber}</Button> : null}
                </div>
              ))}
            </aside>
               </div>
             </details>
             <div className="sticky bottom-0 flex flex-wrap justify-end gap-2 border-t bg-background py-3">
               <Button variant="outline" onClick={close} disabled={mutation.isPending} data-testid="button-cancel-vra-prompt">Cancel</Button>
               <Button className="bg-violet-600 text-white hover:bg-violet-700" onClick={() => mutation.mutate({ kind: "save" })} disabled={mutation.isPending || !draft.trim() || !dirty} data-testid="button-save-vra-prompt">
                 {mutation.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}Save new version
               </Button>
             </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}